/**
 * Navigate to a URL (internal campaign route or external link)
 */
export interface LinkAction {
  type: 'link';
  /**
   * URL to navigate to. Can be:
   * - A relative path (e.g., "/splash/rippling")
   * - A full URL (starting with http:// or https://)
   */
  url: string;
  /**
   * Optional UTM parameters for campaign tracking.
   * These are used to construct the tracking URL.
   * - utm_campaign: Used as the campaign identifier for RipplingCTALink
   * - utm_source: Traffic source (default: "wiw-app")
   * - utm_medium: Marketing medium (default: "link")
   */
  utmParams?: UtmParams;
}

/**
 * UTM parameters for tracking campaign attribution.
 * These override any UTM parameters present in the page URL.
 */
export interface UtmParams {
  /** Campaign name (maps to LT_Campaign__c in Rippling payload) */
  utm_campaign?: string;
  /** Content identifier (maps to LT_Content__c in Rippling payload) */
  utm_content?: string;
  /** Marketing medium (maps to LT_Medium__c in Rippling payload) */
  utm_medium?: string;
  /** Traffic source (maps to LT_Source__c in Rippling payload) */
  utm_source?: string;
  /** Search term (maps to LT_Term__c in Rippling payload) */
  utm_term?: string;
}

/**
 * Open the Rippling Demo Time modal
 */
export interface DemoModalAction {
  type: 'demo-modal';
  /**
   * Optional queue name override. If not provided, uses default logic
   * based on employee count (WiW SB, WiW MM, WiW ENT).
   * Example values: "WiW SB", "WiW MM", "WiW ENT", "WiW"
   */
  queueNameOverride?: string;
  /**
   * Optional lead routing ID override for Salesforce.
   * Default: "701Ud00000SyLMVIA3"
   */
  leadRoutingOverride?: string;
  /**
   * Optional UTM parameters for campaign tracking.
   * These override any UTM parameters present in the page URL.
   */
  utmParams?: UtmParams;
  /**
   * Optional flag to enable phone number collection before opening demo.
   * If true, users without a phone_number will be prompted to provide one.
   * Default: false
   */
  requirePhoneCollection?: boolean;
}

/**
 * Discriminated union for CTA action types.
 * New action types can be added here without breaking existing configurations.
 */
export type CTAAction = LinkAction | DemoModalAction;

/**
 * Configuration interface for the Configurable Rippling Banner component.
 * This interface defines the structure of the LaunchDarkly flag variation
 * that controls banner content, analytics tracking, and dismissal behavior.
 */
export interface RipplingBannerConfig {
  /**
   * Optional banner headline text displayed prominently at the top.
   * If empty or missing, the h3 element will not render.
   */
  title?: string;

  /**
   * Optional banner body text providing additional details or context.
   * If empty or missing, the p element will not render.
   */
  body?: string;

  /**
   * Optional text for the call-to-action button.
   * If empty or missing (or if ctaLink/ctaAction is missing), the CTA will not render.
   */
  ctaText?: string;

  /**
   * @deprecated Use ctaAction instead. Kept for backwards compatibility.
   * If ctaAction is provided, this field is ignored.
   *
   * Optional link destination for the CTA button.
   * Can be either:
   * - A campaign identifier (e.g., "dec-payroll-banner") for RipplingCTALink routing
   * - A full URL (starting with http:// or https://) for direct navigation
   * If empty or missing (or if ctaText is missing), the CTA will not render.
   */
  ctaLink?: string;

  /**
   * Structured CTA action configuration. Takes precedence over ctaLink.
   * Extensible for future action types.
   *
   * Supported action types:
   * - 'link': Navigate to a URL or campaign route
   * - 'demo-modal': Open the Rippling Demo Time modal
   */
  ctaAction?: CTAAction;

  /**
   * Optional PostHog event name to capture when the CTA is clicked.
   * If empty or missing, no analytics event will fire on CTA click.
   */
  phCtaEvent?: string;

  /**
   * Required PostHog event name to capture when the banner is dismissed.
   * This event fires when the user clicks the close button.
   */
  phDismissEvent: string;

  /**
   * Required PostHog event name to capture when the banner becomes visible.
   * This event fires when the banner is displayed to the user.
   */
  phViewEvent: string;

  /**
   * Required cookie name used to track banner dismissal state.
   * When the user dismisses the banner, a cookie with this name is set
   * to prevent the banner from reappearing. Must be a valid cookie name
   * (alphanumeric characters, hyphens, and underscores).
   */
  cookieName: string;
}

/**
 * Type guard to validate if a CTAAction is valid.
 * Checks that the action has a valid type and required fields for that type.
 *
 * @param action - The action to validate
 * @returns True if the action is valid, false otherwise
 */
/**
 * Helper to validate utmParams object structure.
 * Returns true if utmParams is valid or undefined.
 */
function isValidUtmParams(utmParams: unknown): boolean {
  if (utmParams === undefined) {
    return true;
  }
  if (typeof utmParams !== 'object' || utmParams === null || Array.isArray(utmParams)) {
    return false;
  }
  const params = utmParams as Record<string, unknown>;
  const validUtmKeys = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_source', 'utm_term'];
  for (const key of validUtmKeys) {
    if (params[key] !== undefined && typeof params[key] !== 'string') {
      return false;
    }
  }
  return true;
}

export function isValidCTAAction(action: unknown): action is CTAAction {
  if (!action || typeof action !== 'object' || Array.isArray(action)) {
    return false;
  }

  const actionObj = action as Record<string, unknown>;

  if (actionObj.type === 'link') {
    // LinkAction requires a non-empty url string
    if (typeof actionObj.url !== 'string' || actionObj.url.trim() === '') {
      return false;
    }
    // Validate optional utmParams
    return isValidUtmParams(actionObj.utmParams);
  }

  if (actionObj.type === 'demo-modal') {
    // DemoModalAction has no required fields beyond type
    // Optional fields: queueNameOverride, leadRoutingOverride, utmParams, requirePhoneCollection
    if (actionObj.queueNameOverride !== undefined && typeof actionObj.queueNameOverride !== 'string') {
      return false;
    }
    if (actionObj.leadRoutingOverride !== undefined && typeof actionObj.leadRoutingOverride !== 'string') {
      return false;
    }
    if (actionObj.requirePhoneCollection !== undefined && typeof actionObj.requirePhoneCollection !== 'boolean') {
      return false;
    }
    // Validate optional utmParams
    return isValidUtmParams(actionObj.utmParams);
  }

  // Unknown action type
  return false;
}

/**
 * Type guard to validate if a value is a valid RipplingBannerConfig.
 * Checks that all required fields are present and non-empty after trimming.
 *
 * @param config - The value to validate
 * @returns True if the config is valid, false otherwise
 */
export function isValidBannerConfig(config: any): config is RipplingBannerConfig {
  // Handle null, undefined, and non-object values
  if (!config || typeof config !== 'object' || Array.isArray(config)) {
    return false;
  }

  // Check required fields are present and non-empty after trimming
  const requiredFields: (keyof RipplingBannerConfig)[] = ['phDismissEvent', 'phViewEvent', 'cookieName'];

  for (const field of requiredFields) {
    const value = config[field];

    // Field must be a string
    if (typeof value !== 'string') {
      return false;
    }

    // Field must not be empty after trimming
    if (value.trim() === '') {
      return false;
    }
  }

  // If ctaAction is provided, validate it
  if (config.ctaAction !== undefined) {
    return isValidCTAAction(config.ctaAction);
  }

  return true;
}
